import type { PlaidItem } from '@/api/generated/model';
import { createPlaidItem, createPlaidLinkToken } from '@/api/generated/plaid/plaid';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/i18n/language-hooks';
import { useCallback, useState } from 'react';
import { openPlaidLink } from './openPlaidLink';

/**
 * Runs the whole Plaid Link handshake: link token → Link modal → public-token exchange. Resolves
 * with the linked item, or `null` if the user dismissed Link.
 *
 * The exchange happens on the server, which stores the access token encrypted and immediately
 * syncs the item's accounts and transactions into the staging tables.
 */
export function useConnectPlaid() {
  const { t } = useLanguage();
  const [isConnecting, setIsConnecting] = useState(false);

  const connect = useCallback(async (): Promise<PlaidItem | null> => {
    setIsConnecting(true);
    try {
      const { linkToken } = await createPlaidLinkToken();
      const publicToken = await openPlaidLink(linkToken);
      if (!publicToken) return null;
      return await createPlaidItem({ publicToken });
    } catch (error) {
      toast({
        title: t('plaid.toast.connectError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [t]);

  return { connect, isConnecting };
}
